import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import BopBackLink from '../../components/BopBackLink';
import AccessoNegato from '../../components/permessi/AccessoNegato';
import GrigliaPermessi, {
  VoceGriglia, FlagsPermesso, CampoPermesso,
  TUTTO_ON, SOLA_LETT, TUTTO_OFF, normalizzaFlags, flagsUguali,
} from '../../components/permessi/GrigliaPermessi';
import { usePermesso } from '../../context/PermessiContext';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import { apiFetch } from '../../lib/api';
import '../../styles/impostazioni.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

interface VoceApi extends FlagsPermesso {
  modulo: string;
  label:  string;
}

interface GruppoApi { gruppo: string; moduli: VoceApi[] }
interface MacroApi  { macro:  string; gruppi: GruppoApi[] }

interface Assegnazione {
  utenteId:    number;
  utenteNome:  string;
  utenteEmail: string;
  societaId:   number;
  societaNome: string;
}

interface ProfiloDettaglio {
  id:           number;
  nome:         string;
  descrizione:  string | null;
  attivo:       boolean;
  macroGruppi:  MacroApi[];
  assegnazioni: Assegnazione[];
}

// ── Componente ────────────────────────────────────────────────────────────────

export default function ProfiloDettaglioPage() {
  const { isSuperadmin } = usePermesso();
  const { id } = useParams<{ id: string }>();

  const [profilo,   setProfilo]   = useState<ProfiloDettaglio | null>(null);
  const [voci,      setVoci]      = useState<VoceGriglia[]>([]);
  const [origVoci,  setOrigVoci]  = useState<Record<string, FlagsPermesso>>({});
  const [dati,      setDati]      = useState({ nome: '', descrizione: '', attivo: true });
  const [snapshot,  setSnapshot]  = useState('');

  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState('');
  const [error,   setError]   = useState('');

  const permessiDirty = voci.some(v => v.dirty);
  const datiDirty     = snapshot !== '' && JSON.stringify(dati) !== snapshot;
  const isDirty       = permessiDirty || datiDirty;

  useDirtyForm(isDirty, profilo ? `Profilo ${profilo.nome}` : 'Profilo permessi');

  const carica = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    setMsg('');
    try {
      // @api GET /api/profili/:id -> {"success":"true","data":"ProfiloDettaglio"}
      const d = await apiFetch<{ data: ProfiloDettaglio }>(`/api/profili/${id}`);

      const flat: VoceGriglia[] = [];
      const orig: Record<string, FlagsPermesso> = {};
      for (const m of d.data.macroGruppi) {
        for (const g of m.gruppi) {
          for (const v of g.moduli) {
            flat.push({ ...v, macro: m.macro, gruppo: g.gruppo, dirty: false });
            orig[v.modulo] = {
              canView: v.canView, canEdit: v.canEdit,
              canDelete: v.canDelete, canExport: v.canExport,
            };
          }
        }
      }

      const nuoviDati = {
        nome:        d.data.nome,
        descrizione: d.data.descrizione ?? '',
        attivo:      d.data.attivo,
      };

      setProfilo(d.data);
      setVoci(flat);
      setOrigVoci(orig);
      setDati(nuoviDati);
      setSnapshot(JSON.stringify(nuoviDati));
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => {
    if (!isSuperadmin) return;
    carica();
  }, [isSuperadmin, carica]);

  // ── Modifica griglia ────────────────────────────────────────────────────
  const marcaDirty = (v: VoceGriglia): VoceGriglia => ({
    ...v,
    dirty: !flagsUguali(v, origVoci[v.modulo] ?? TUTTO_OFF),
  });

  const toggle = (modulo: string, campo: CampoPermesso) => {
    setVoci(prev => prev.map(v => {
      if (v.modulo !== modulo) return v;
      return marcaDirty({ ...v, ...normalizzaFlags({ ...v, [campo]: !v[campo] }) });
    }));
  };

  const applicaGruppo = (filtro: (v: VoceGriglia) => boolean, flags: FlagsPermesso) => {
    setVoci(prev => prev.map(v => (filtro(v) ? marcaDirty({ ...v, ...normalizzaFlags(flags) }) : v)));
  };

  // ── Salvataggio ─────────────────────────────────────────────────────────
  const salva = async () => {
    if (!id) return;
    if (!dati.nome.trim()) { setError('Il nome del profilo è obbligatorio'); return; }

    setSaving(true);
    setMsg('');
    setError('');
    try {
      if (datiDirty) {
        // @api PUT /api/profili/:id {"nome":"string","descrizione":"string","attivo":"boolean"} -> {"success":"true"}
        await apiFetch(`/api/profili/${id}`, {
          method: 'PUT',
          body: JSON.stringify({
            nome:        dati.nome.trim(),
            descrizione: dati.descrizione.trim() || null,
            attivo:      dati.attivo,
          }),
        });
      }

      let propagati = 0;
      if (permessiDirty) {
        // @api PUT /api/profili/:id/permessi {"permessi":"VoceApi[]"} -> {"success":"true","data":"{utentiAggiornati:number}"}
        const r = await apiFetch<{ data: { utentiAggiornati: number } }>(`/api/profili/${id}/permessi`, {
          method: 'PUT',
          body: JSON.stringify({
            permessi: voci.map(({ modulo, canView, canEdit, canDelete, canExport }) =>
              ({ modulo, canView, canEdit, canDelete, canExport })),
          }),
        });
        propagati = r.data.utentiAggiornati;
      }

      const nuoviOrig: Record<string, FlagsPermesso> = {};
      voci.forEach(v => {
        nuoviOrig[v.modulo] = {
          canView: v.canView, canEdit: v.canEdit, canDelete: v.canDelete, canExport: v.canExport,
        };
      });
      setOrigVoci(nuoviOrig);
      setVoci(prev => prev.map(v => ({ ...v, dirty: false })));
      setSnapshot(JSON.stringify(dati));

      setMsg(propagati > 0
        ? `Profilo salvato. Modifiche propagate a ${propagati} assegnazioni.`
        : 'Profilo salvato.');
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  if (!isSuperadmin) {
    return (
      <Layout>
        <AccessoNegato messaggio="Solo un Superadmin può gestire i profili permessi." />
      </Layout>
    );
  }

  const vociAttive = voci.filter(v => v.canView).length;

  return (
    <Layout>
      <BopBackLink to="/utenti/profili" label="Torna ai profili" />

      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2>{profilo?.nome ?? 'Profilo permessi'}</h2>
          <p>
            {vociAttive} voci abilitate su {voci.length}. Ogni modifica salvata raggiunge
            subito tutti gli utenti che usano questo profilo, tranne dove hanno una deroga.
          </p>
        </div>
      </div>

      {error && <div className="bop-inline-error">{error}</div>}
      {msg   && <div className="bop-inline-success">{msg}</div>}

      {loading ? (
        <div className="bop-card"><div className="bop-empty-state">Caricamento…</div></div>
      ) : !profilo ? (
        <div className="bop-card"><div className="bop-empty-state">Profilo non trovato.</div></div>
      ) : (
        <>
          {/* ── Dati profilo ───────────────────────────────────────────── */}
          <div className="bop-card">
            <div className="bop-card__header">
              <h3 className="bop-card__title">Dati profilo</h3>
            </div>
            <div className="bop-card__body">
              <div className="impostazioni-profilo-form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Nome *</label>
                  <input
                    className="bop-input"
                    value={dati.nome}
                    onChange={e => setDati({ ...dati, nome: e.target.value })}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Descrizione</label>
                  <input
                    className="bop-input"
                    value={dati.descrizione}
                    onChange={e => setDati({ ...dati, descrizione: e.target.value })}
                  />
                </div>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">
                  <input
                    type="checkbox"
                    checked={dati.attivo}
                    onChange={e => setDati({ ...dati, attivo: e.target.checked })}
                    className="impostazioni-permessi-checkbox impostazioni-permessi-checkbox-view"
                  />
                  {' '}Profilo attivo (assegnabile a nuovi utenti)
                </label>
              </div>
            </div>
          </div>

          {/* ── Azioni globali griglia ─────────────────────────────────── */}
          <div className="impostazioni-permessi-actions-global">
            <button className="bop-btn bop-btn-ghost bop-btn-sm"
              onClick={() => applicaGruppo(() => true, TUTTO_ON)}>
              Tutto pieno
            </button>
            <button className="bop-btn bop-btn-ghost bop-btn-sm"
              onClick={() => applicaGruppo(() => true, SOLA_LETT)}>
              Solo lettura
            </button>
            <button className="bop-btn bop-btn-ghost bop-btn-sm impostazioni-permessi-btn-danger"
              onClick={() => applicaGruppo(() => true, TUTTO_OFF)}>
              Azzera tutto
            </button>
          </div>

          <GrigliaPermessi
            voci={voci}
            onToggle={toggle}
            onApplicaGruppo={applicaGruppo}
          />

          {/* ── Assegnazioni ───────────────────────────────────────────── */}
          <div className="bop-card impostazioni-profilo-assegnazioni">
            <div className="bop-card__header">
              <h3 className="bop-card__title">
                Chi usa questo profilo ({profilo.assegnazioni.length})
              </h3>
            </div>
            <div className="bop-card__body">
              {profilo.assegnazioni.length === 0 ? (
                <div className="bop-empty-state">
                  Nessuna assegnazione. Assegna il profilo dalla scheda permessi di un utente.
                </div>
              ) : (
                <div className="bop-table-wrapper">
                  <table className="bop-table">
                    <thead>
                      <tr>
                        <th>Utente</th>
                        <th>Email</th>
                        <th>Società</th>
                      </tr>
                    </thead>
                    <tbody>
                      {profilo.assegnazioni.map(a => (
                        <tr key={`${a.utenteId}|${a.societaId}`}>
                          <td>{a.utenteNome}</td>
                          <td className="bop-text-muted">{a.utenteEmail}</td>
                          <td>{a.societaNome}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* ── Salva ──────────────────────────────────────────────────── */}
          <div className="impostazioni-permessi-footer">
            <button className="bop-btn bop-btn-primary" onClick={salva} disabled={saving || !isDirty}>
              {saving ? 'Salvo…' : 'Salva profilo'}
            </button>
            {isDirty && !saving && (
              <span className="impostazioni-permessi-msg-warning">
                Modifiche non salvate
                {profilo.assegnazioni.length > 0 &&
                  ` — al salvataggio raggiungeranno ${profilo.assegnazioni.length} assegnazioni`}
              </span>
            )}
          </div>
        </>
      )}
    </Layout>
  );
}
